import React, { useCallback, useEffect, useRef, useState } from "react";
import { Transition } from "react-transition-group";
import cx from "classnames";

import styles from "./Popover.scss";

export interface PopoverRenderProps {
  closePopover: () => void;
  openPopover: () => void;
  triggerRef: React.MutableRefObject<HTMLButtonElement | null>;
  onFocus?: React.FocusEventHandler;
}

interface PopoverProps {
  input: (renderProps: PopoverRenderProps) => React.ReactElement | JSX.Element;
  content: (contentArgs: PopoverRenderProps) => React.ReactElement;
  onOpen?: () => void;
  onClose?: () => void;
  popperWidth?: number;
  onLeft?: boolean;
  isDisabled?: boolean;
}

export const duration = 150;

const defaultStyle = {
  transition: `opacity ${duration}ms ease-in-out`,
  opacity: 0
};

const transitionStyles = {
  entering: { opacity: 0 },
  entered: { opacity: 1 },
  exiting: {},
  unmounted: {}
};

const Popover = ({
  input,
  content,
  onOpen,
  onClose,
  popperWidth = 0,
  onLeft = false,
  isDisabled = false
}: PopoverProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const componentWrapperRef = useRef<HTMLDivElement>(null);

  const callOnOpenAndClose = useCallback(
    (currentIsOpen: boolean, newIsOpen: boolean) => {
      if (currentIsOpen !== newIsOpen) {
        if (newIsOpen && onOpen) {
          onOpen();
        } else {
          if (onClose) {
            onClose();
          }
        }
      }
    },
    [onOpen, onClose]
  );

  const handleVisibilityDependingIfElementIsInWrapper = useCallback(
    (element: EventTarget) => {
      setIsOpen(state => {
        const currentIsOpen = state;
        const newIsOpen = isElementInsideWrapper(element);

        callOnOpenAndClose(currentIsOpen, newIsOpen);
        return currentIsOpen !== newIsOpen ? newIsOpen : currentIsOpen;
      });
    },
    [callOnOpenAndClose]
  );

  const handleClickOutside = useCallback(
    (e: MouseEvent) => {
      if (e.target) {
        handleVisibilityDependingIfElementIsInWrapper(e.target);
      }
    },
    [handleVisibilityDependingIfElementIsInWrapper]
  );

  const handleFocus = useCallback(
    (e: FocusEvent) => {
      if (e.target) {
        handleVisibilityDependingIfElementIsInWrapper(e.target);
      }
    },
    [handleVisibilityDependingIfElementIsInWrapper]
  );

  const closePopover = useCallback(() => {
    setIsOpen(false);
  }, []);

  const handleKeyPress = useCallback(
    (e: KeyboardEvent) => {
      if (e.target instanceof HTMLElement) {
        const { closeonshifttab, closeontab } = e.target.dataset;

        const shouldCloseOnShiftTab = closeonshifttab === "true" && e.shiftKey && e.key === "Tab";
        const shouldCloseOnTab = closeontab === "true" && !e.shiftKey && e.key === "Tab";

        if (shouldCloseOnShiftTab || shouldCloseOnTab) {
          closePopover();
        }
      }
    },
    [closePopover]
  );

  useEffect(() => {
    document.addEventListener("mousedown", handleClickOutside);
    document.addEventListener("focus", handleFocus);
    document.addEventListener("keydown", handleKeyPress);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
      document.removeEventListener("focus", handleFocus);
      document.removeEventListener("keydown", handleKeyPress);
    };
  }, [handleClickOutside, handleFocus, handleKeyPress]);

  const openPopover = () => {
    setIsOpen(true);
  };

  const isElementInsideWrapper = (target: EventTarget) => {
    const componentWrapper = componentWrapperRef.current;
    return !!(componentWrapper && componentWrapper.contains(target as Node));
  };

  const getPopoverRenderProps = () => ({
    closePopover,
    openPopover,
    triggerRef
  });

  const renderInput = () => {
    return input({
      onFocus: (event: React.FocusEvent) => {
        handleFocus(event as unknown as FocusEvent);
      },
      ...getPopoverRenderProps()
    });
  };

  const renderContent = () => {
    return content(getPopoverRenderProps());
  };

  const getOnLeftStyles = () => {
    const triggerWidth = (triggerRef.current && triggerRef.current.offsetWidth) || 0;
    const width = popperWidth ? popperWidth : 0;
    return {
      marginLeft: `-${(width - triggerWidth) / 10}rem`
    };
  };

  return (
    <div
      className={cx(...[styles].map(c => c.componentWrapper), {
        [styles.fixedComponentWrapper]: !!popperWidth
      })}
      ref={componentWrapperRef}
    >
      {renderInput()}
      {!isDisabled && (
        <Transition in={isOpen} timeout={duration} unmountOnExit>
          {state => {
            const stylesForTranstition =
              state in transitionStyles &&
              typeof transitionStyles[state as keyof typeof transitionStyles] === "object"
                ? transitionStyles[state as keyof typeof transitionStyles]
                : {};

            return (
              <div
                style={{
                  ...defaultStyle,
                  ...stylesForTranstition,
                  ...(popperWidth
                    ? {
                        maxWidth: `${popperWidth / 10}rem`
                      }
                    : {}),
                  ...(onLeft ? getOnLeftStyles() : {}),
                  ...(state === "exiting" ? { pointerEvents: "none" } : {})
                }}
                className={cx(styles.popper)}
                data-testid="popover"
              >
                {renderContent()}
              </div>
            );
          }}
        </Transition>
      )}
    </div>
  );
};

export default Popover;
